// Domain related to simple orders and general order types. Will be extended by particular order types
import { SecurityInfo, SymbolType } from '../../../domain/symbol.domain';
import { Currency, MarketType } from '../../../domain/market.domain';
import { AccountType, TradingPlatform, AccountPlatform, TradingAccount, AccountDetailsResponse } from '../../../domain/account.domain';
import { FormAction } from './form-action.domain';
import {
  EquityOrderAction,
  GroupOrderStatusType,
  InsiderType,
  LifeCycleType,
  OptionOrderAction,
  OrderStatusType,
  PricePlan,
  Strategy
} from './order.enum';
import { Conditions, OrderConditions } from './conditions.domain';
import type {
  EquityLikeExtendedTerms,
  GicLikeExtendedTerms,
  InsiderInfo,
  LegLikeExtendedTerms,
  MutualFundLikeExtendedTerms,
  MutualFundLikeOrderExtendedTerms,
  MutualFundLikeSecurityInfo,
  OptionLikeExtendedTerms,
  OrderDetail,
  OrderEstimate,
  MultiLegExtendedTerms,
  OrderGoodTilValue,
  WithOrderActionFlags,
  WithOrderStatus
} from './order-shared.types';

// TODO REMOVE THIS - DUPLICATE with OrderRequest
export interface Order<T extends  OrderTerms = OrderTerms> {
  conditionFilter: string;
  orderTerms: T;
  acknowledgements?: {
    conditions: Conditions;
  };
  tradingCredential?: {
    tradingPassword: string;
    rememberPassword?: boolean;
  };
}

/**
 * OrderRequest
 * The server-side model object to request on captureRequest (step1)
 */
export interface OrderRequest<T = OrderTermsRequest> {
  conditionFilter: string;
  orderTerms: T;
  acknowledgements?: {
    conditions: Conditions;
  };
  tradingCredential?: TradingCredential;
}

export interface OrderCapture<T extends OrderTerms = OrderTerms> {
  conditions: OrderConditions;
  orderTerms: T;
}

export interface OrderParameters {
  orderType: string;
  actionType: string;
  orderKey: string;
  operation: string;
  action: string;
  symbol: string;
  market: string;
  accountId: string;
  quantity: string;
  optionNonStandard: string;
  securityType: string;
  price: string;
  orderView: boolean;
  reverse: boolean;
  simplifiedOrder: boolean;
}

export interface OrderTerms {
  accountInfo: OrderAccountInfo;
  afterMarket: boolean;
  createdById?: string;
  order: Array<OrderItem>;
  orderToken?: OrderToken;
  pricePlan: PricePlan;
  strategy: Strategy;
  orderSubmissionDate?: Date;
  multilegEntry?: { extendedTerms: MultiLegExtendedTerms };
}

export interface OrderPreferences {
  optionType: string;
  quantity: number;
  quantityIncrement: number;
  stockType: string;
  contract: number;
  contractsIncrement: number;
}

export interface OrderAccountInfo {
  accountId: string;
  accountNumber: string;
  acctType: AccountType;
  currency: Currency;
  accountPlatform: AccountPlatform;
  orderBuyingPower: number;
  tradingPlatform: TradingPlatform;
}

export interface OrderToken {
  orderKeyEncoded: string;
}

// TODO: should be split into two types - one for order request and another for existing orders
export interface OrderItem<T extends SecurityExtendedTerms = SecurityExtendedTerms> extends WithOrderActionFlags, WithOrderStatus {
  clientInfo?: InsiderInfo;
  extendedTerms?: T;
  security?: T extends MutualFundLikeExtendedTerms ? MutualFundLikeSecurityInfo : SecurityInfo;
  details?: OrderDetail; // TODO: used in order status / change/ cancel
  grossFilledAmount?: number;
  id?: string;
  lifeCycle?: LifeCycleType; // TODO: not used on the ui
  orderEstimate?: OrderEstimate;
  omsOrderNumber?: string;
  orderNumber?: string;
  orderGroupStatus?: GroupOrderStatusType;
  orderStatus?: OrderStatusType;
  percentFilled?: number;
  quantity: number;
  dollarAmt?: number;
  legChanged?: boolean;
  slipOrderDetail?: SlipOrderDetail;
}

export interface SlipOrderDetail {
  slipSell: boolean;
}

export interface OrderPriceAmount {
  priceLimit: number | undefined;
  priceTrigger: number | undefined;
  deltaLimit?: number | undefined;
  deltaTrigger?: number | undefined;
  deltaPercent?: number | undefined;
}

export interface PriceEstimate {
  deltaEstimate: number;
  deltaLimitEstimate: number;
}

export type SecurityExtendedTerms =
  | EquityLikeExtendedTerms
  | OptionLikeExtendedTerms
  | MutualFundLikeOrderExtendedTerms
  | LegLikeExtendedTerms
  | GicLikeExtendedTerms;

export type PriceSelection =
  'MARKET'
  | 'LIMIT'
  | 'OPTION_LIMIT'
  | 'STOP_MARKET'
  | 'STOP_LIMIT'
  | 'TRAILING_STOP_MARKET'
  | 'TRAILING_STOP_LIMIT'
  | 'EVEN'
  | 'NET_DEBIT'
  | 'NET_CREDIT'
  | 'RECURRING_INVESTMENT';

export enum FractionalType {
  WHOLESHARE = 'WHOLESHARE',
  FRACTIONAL = 'FRACTIONAL',
  NOTIONAL = 'NOTIONAL'
}

export interface PriceInputDisplay {
  priceLimit: boolean;
  priceTrigger: boolean;
  deltaLimit: boolean;
  deltaTrigger: boolean;
  deltaPercent: boolean;
}

export type PriceSelectDisplay = Record<Exclude<PriceSelection, 'EVEN' | 'NET_DEBIT' | 'NET_CREDIT' | 'RECURRING_INVESTMENT'>, PriceInputDisplay>;

export interface GoodTilDisplay {
  showDay: boolean;
  showSpecify: boolean;
  showCancel: boolean;
  showEXTMarkets: boolean;
}

export type OrderGoodTil =
  OrderGoodTilValue;

export type PriceSelectionToGoodTilDisplay = Record<Exclude<PriceSelection, 'RECURRING_INVESTMENT'>, GoodTilDisplay>;

// TODO REMOVE THIS OR OrderTerms - DUPLICATE with OrderTerms@88
export interface OrderTermsRequest {
  accountInfo: { accountId: string };
  afterMarket: boolean;
  createdById?: string;
  order: Array<OrderItem>;
  orderToken?: OrderToken;
  pricePlan: PricePlan;
  strategy: Strategy;
  orderSubmissionDate?: Date;
  multilegEntry?: { extendedTerms: MultiLegExtendedTerms }; // TODO check need for refactor
}

export interface CancelOrderRequest {
  operation: 'RAPID';
  tradingCredential?: TradingCredential;
}

export interface CancelOrderParameters {
  orderKey: string;
  cancelOrderRequest: CancelOrderRequest;
}

export interface TradingCredential {
  tradingPassword: string;
  rememberPassword?: boolean;
}

export interface EquityOptionsCommonModel {
  accountId?: string; // TODO: this is temporary
  orderPriceAmount: OrderPriceAmount; // are they all optional?
  shareholderStatus: InsiderType;
  orderQuantity: number;
  orderAllOrNone: boolean;
  orderPrice: PriceSelection;  // TODO FIX and use security specific enums
  orderGoodTil: OrderGoodTil;
  orderGoodTilDate?: Date;
  selectedSymbol: SecurityInfo;
  orderAction: EquityOrderAction | OptionOrderAction;
  nonStandard?: boolean;
}

export interface TradingAccountDetails {
  account: TradingAccount;
  accountDetails: AccountDetailsResponse;
}

export interface TradingAction {
  formAction: FormAction;
  orderCaptureResponse?: OrderCapture<OrderTerms>;
  tradingCredential?: TradingCredential;
}

// used on cancel form
export interface CancelSymbol {
  symbol: string;
  market: MarketType;
  type: SymbolType;
}

export type {
  BaseExtendedTerms,
  EquityLikeExtendedTerms,
  GicLikeExtendedTerms,
  InsiderInfo,
  LegLikeExtendedTerms,
  MutualFundLikeExtendedTerms,
  MutualFundLikeOrderExtendedTerms,
  MutualFundLikeSecurityInfo,
  OptionLikeExtendedTerms,
  OrderDetail,
  OrderEstimate,
  OrderExtendedTerms,
  OrderFill,
  OrderGoodTilValue,
  WithOrderActionFlags,
  WithOrderStatus
} from './order-shared.types';

